import React, { useState, useEffect, useRef } from 'react'

import HoverTooltip from '../../../../../../components/HoverTooltip'
import MoneyInput from '../../../../../../components/form/MoneyInput'
import CurrencySelector from '../../../../../../components/currency/CurrencySelector'
import CurrencyExchangeRateInput from '../../../../../../components/currency/CurrencyExchangeRateInput'
import LoadingIcon from '../../../../../../components/LoadingIcon'

import Section from '../../../../../../components/Section'
import {
    PurchaseLot,
    PurchaseLotItem,
    ContractPerformance,
    LongTermContractVolumes,
} from '../../../types'

import {
    CurrencyList,
    Okei,
    PlanPositionRow,
} from '../../../../Plans/Positions/Edit/types'

import {
    purchaseTypeInfo,
    purchaseInfo,
    planInfo,
    ContractPositionsInfoError,
} from '../types'

import Checkbox from '../../../../../../components/form/Checkbox'
import PurchaseLotPositionTableRow from './PurchaseLotTable/PurchaseLotPositionTableRow'
import { OrganizationInfo } from '../../../../Org/Home/types'

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-' // Обрабатываем пустые значения

    const parsedNum = typeof num === 'number' ? num : parseFloat(num)

    if (isNaN(parsedNum)) {
        return '-' // Обрабатываем некорректные значения
    }

    return parsedNum
        .toFixed(2) // Два знака после запятой
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') // Разделяем тысячные пробелами
        .replace('.', ',') // Заменяем точку на запятую
}

type Props = {
    theme: string
    purchaseAction: boolean
    purchaseLotGuid: string | undefined
    purchaseLot: PurchaseLot
    currencyList: CurrencyList[]
    purchaseLotItems: PurchaseLotItem[]
    isLoadingPurchaseLotItems: boolean
    orgData: OrganizationInfo | undefined
}

const PurchaseLotPositionInfo: React.FC<Props> = (props) => {
    const [okeiList, setOkeiList] = useState<Okei[]>([])

    const getOkeiList = async (): Promise<Okei[]> => {
        return fetch(process.env.REACT_APP_API_URL + '/api/backend/v1/okei', {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на способы закупок')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const okei = await getOkeiList()
                if (okei) {
                    setOkeiList(okei)
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    const PurchaseLotItemsQtySum = Number(
        props.purchaseLotItems
            .reduce((sum, item) => sum + Number(item.qty ?? 0), 0)
            .toFixed(2)
    )

    return (
        <Section title="Информация о закупаемых товарах, работах, услугах">
            <div className="flex flex-col w-full gap-3 items-start justify-start ">
                <div
                    className={`flex flex-col w-full h-fit items-center justify-center bg-[#e4e6e8] dark:bg-[#242225] rounded`}
                >
                    <div className="flex flex-row gap-1 p-1 w-full h-[40px] text-[11px] rounded-t text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033] items-center justify-center">
                        {/* тут кнопки */}
                        <div className="flex flex-1 w-full h-full items-center justify-center text-center"></div>
                        <div className="flex flex-col w-48 gap-1 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] items-center justify-center text-center border-r border-r-[#b8b8b8] dark:border-r-[#464447]">
                            Позиций <br />
                            {props.purchaseLotItems.length}
                        </div>
                        <div className="flex flex-col w-48 gap-1 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] items-center justify-center text-center">
                            Количество (объем) <br />
                            {PurchaseLotItemsQtySum}
                        </div>
                    </div>
                    <div className="flex flex-row gap-1 w-full h-[40px] p-1 px-1 items-center justify-center text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                        <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        <div className="flex w-10 h-full items-center justify-center text-center">
                            №
                        </div>
                        <div className="flex flex-1 items-center justify-center ">
                            Наименование
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Количество (объем)
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Единица измерения
                        </div>
                        <div className="flex w-28 items-center justify-center text-center">
                            ОКПД2
                        </div>
                        <div className="flex w-28 items-center justify-center text-center">
                            ОКВЭД2
                        </div>
                    </div>
                    <div className="flex flex-col w-full h-full gap-1 py-1 overflow-y-auto custom-scrollbar">
                        {props.isLoadingPurchaseLotItems ? (
                            <div className="flex w-full h-full items-center justify-center">
                                <LoadingIcon />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1 text-[11px] items-start justify-center">
                                {props.purchaseLotItems
                                    ? props.purchaseLotItems.map(
                                          (row, index) => (
                                              <PurchaseLotPositionTableRow
                                                  key={row.ordinal_number}
                                                  purchaseLotItem={row}
                                                  currencyList={
                                                      props.currencyList
                                                  }
                                                  theme={props.theme}
                                                  purchaseLot={
                                                      props.purchaseLot
                                                  }
                                                  okeiList={okeiList}
                                                  purchaseAction={
                                                      props.purchaseAction
                                                  }
                                                  orgData={props.orgData}
                                              />
                                          )
                                      )
                                    : ''}
                            </div>
                        )}
                    </div>

                    {/* footer */}
                    <div className="flex flex-row gap-1 w-full h-[40px] text-[11px] p-1 rounded-b items-center justify-start text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                        <div className="flex flex-1 flex-row gap-3 px-5 h-full items-center justify-start text-center">
                            {/* тут кнопки*/}
                        </div>
                        <div className="flex flex-1 w-full h-full items-center justify-center text-center"></div>
                        <div className="flex flex-col w-48 gap-1 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] items-center justify-center text-center border-r border-r-[#b8b8b8] dark:border-r-[#464447]">
                            Позиций <br />
                            {props.purchaseLotItems.length}
                        </div>
                        <div className="flex flex-col w-48 gap-1 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] items-center justify-center text-center">
                            Количество (объем) <br />
                            {PurchaseLotItemsQtySum}
                        </div>
                    </div>
                </div>
            </div>
        </Section>
    )
}

export default PurchaseLotPositionInfo
